import styled from "styled-components";
import { Body, Mini } from "../../styles/stringStyles";
import { useStyles } from "../../styles/theme";
import { hexWithOpacity } from "../../utils/hexWithOpacity";
import { IconButton } from "../buttons/IconButton";

type Props = {
  label?: string;
  date?: string;
  onChange?: (date: string) => void;
  error?: string;
  withoutError?: boolean;
  tag?: string;
};

export const DatePicker = ({
  date,
  onChange,
  label,
  error,
  withoutError,
  tag,
}: Props) => {
  const theme = useStyles();

  return (
    <Container>
      <LabelContainer>
        {label && <StyledMini>{label}</StyledMini>}
        {tag && <TagValue>{tag}</TagValue>}
      </LabelContainer>
      <InputWrapper>
        <DatePickerInput
          type="datetime-local"
          value={date}
          onChange={(event) => onChange?.(event.target.value)}
          placeholder="TT/MM/YYYY"
        />
        <IconWrapper>
          <IconButton
            iconName="Calendar"
            size={20}
            primaryColor={theme.colors.medium}
            noPadding
          />
          <IconButton
            iconName="Clock"
            size={20}
            primaryColor={theme.colors.medium}
            noPadding
          />
        </IconWrapper>
      </InputWrapper>
      {!withoutError && (
        <Error>
          <ErrorText>{error ?? " "}</ErrorText>
        </Error>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
`;

const StyledMini = styled(Body)`
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DatePickerInput = styled.input`
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid
    ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.25)};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  padding: 8px 72px 8px 12px;
  /* hide native picker icons visually but keep click target intact */
  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  &::-webkit-inner-spin-button,
  &::-webkit-clear-button {
    display: none;
  }
  font:
    400 16px/26px Ubuntu,
    sans-serif;
  outline: none;
  box-shadow: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.dark};
    opacity: 0.5;
  }
  &:hover:not(:disabled):not(:focus) {
    border: 1.5px solid ${({ theme }) => theme.colors.medium};
  }
  &:focus {
    outline: none;
    border: 1.5px solid ${({ theme }) => theme.colors.primary};
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const ErrorText = styled(Mini)`
  color: ${({ theme }) => theme.colors.lightRed};
`;

const Error = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 5px;
  padding-right: 10px;
  max-lines: 1;
  height: 20px;
`;

const TagValue = styled(Mini)`
  background-color: ${({ theme }) => hexWithOpacity(theme.colors.medium, 0.1)};
  color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.6)};
  border-radius: 5px;
  padding: 2px 6px;
`;

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding-bottom: 5px;
`;
